import { useState, useEffect, useRef } from 'react';
import type { Landmark } from '../types';
import { IDX } from '../constants';

interface StabilityOptions {
  varianceThreshold: number; // Pixels variance allowed
  stableDurationMs: number; // How long to hold stability
  minPoseConfidence?: number;
  /** Optional extra indices (e.g. ankles in guided capture) — all must be present and visible. */
  extraLandmarkIndices?: number[];
  /** Minimum visibility for `extraLandmarkIndices` (defaults to 0.5). */
  extraLandmarkVisibilityThreshold?: number;
  /** Landmarks must exceed this visibility to count toward stability (same as capture settings). */
  visibilityThreshold?: number;
  /** When this value changes, stability history and timers reset (e.g. next guided view, settings sheet). */
  resetKey?: string | number;
}

interface StabilityResult {
  isStable: boolean;
  variance: number;
  stableDuration: number;
  isPoseDetected: boolean;
}

// Key landmarks to monitor for stability (Shoulders, Hips, Knees)
const KEY_LANDMARKS = [
  IDX.L_SHO, IDX.R_SHO,
  IDX.L_HIP, IDX.R_HIP,
  IDX.L_KNEE, IDX.R_KNEE
];

/**
 * Pose stability for auto-capture. Only `isStable` and `isPoseDetected` drive UI; variance/duration
 * are kept as static fields for API compatibility. Internal timing uses refs only — no interval
 * or per-frame display state — so parent re-renders (e.g. when countdown mounts) cannot nest updates
 * with landmark-driven effects.
 */
export function useStabilityDetection(
  landmarks: Landmark[] | null,
  options: StabilityOptions
): StabilityResult {
  const {
    varianceThreshold,
    stableDurationMs,
    extraLandmarkIndices = [],
    extraLandmarkVisibilityThreshold = 0.5,
    visibilityThreshold = 0,
    resetKey = '',
  } = options;
  const [isStable, setIsStable] = useState(false);
  const stabilityStartRef = useRef<number | null>(null);

  const historyRef = useRef<Array<Landmark[]>>([]);
  const trackedIndicesKeyRef = useRef<string>('');
  const prevKeyCountRef = useRef<number>(-1);
  const prevResetKeyRef = useRef<string | number | null>(null);

  // Hard reset when session key changes (per-view / settings / toolbar)
  useEffect(() => {
    if (prevResetKeyRef.current === null) {
      prevResetKeyRef.current = resetKey;
      return;
    }
    if (resetKey === prevResetKeyRef.current) return;
    prevResetKeyRef.current = resetKey;
    setIsStable(false);
    stabilityStartRef.current = null;
    historyRef.current = [];
    prevKeyCountRef.current = -1;
    trackedIndicesKeyRef.current = '';
  }, [resetKey]);

  useEffect(() => {
    const clearHold = () => {
      stabilityStartRef.current = null;
    };

    if (!landmarks || landmarks.length === 0) {
      clearHold();
      setIsStable((s) => (s ? false : s));
      historyRef.current = [];
      prevKeyCountRef.current = -1;
      return;
    }

    const indices = [...KEY_LANDMARKS];
    for (const idx of extraLandmarkIndices) {
      if (!indices.includes(idx)) {
        indices.push(idx);
      }
    }

    const indicesKey = indices.join(',');
    if (trackedIndicesKeyRef.current !== indicesKey) {
      trackedIndicesKeyRef.current = indicesKey;
      historyRef.current = [];
      prevKeyCountRef.current = -1;
    }

    for (const idx of extraLandmarkIndices) {
      const p = landmarks[idx];
      if (!p || (p.visibility ?? 0) <= extraLandmarkVisibilityThreshold) {
        clearHold();
        setIsStable((s) => (s ? false : s));
        return;
      }
    }

    const keyPoints = indices
      .map((idx) => landmarks[idx])
      .filter(
        (p): p is Landmark =>
          !!p && (p.visibility ?? 0) > visibilityThreshold
      );

    if (keyPoints.length < 4) {
      clearHold();
      setIsStable((s) => (s ? false : s));
      historyRef.current = [];
      prevKeyCountRef.current = -1;
      return;
    }

    // History rows must be comparable — clear if visible keypoint count changed
    if (keyPoints.length !== prevKeyCountRef.current) {
      prevKeyCountRef.current = keyPoints.length;
      historyRef.current = [];
      clearHold();
      setIsStable((s) => (s ? false : s));
    }

    historyRef.current.push(keyPoints);
    if (historyRef.current.length > 10) {
      historyRef.current.shift();
    }

    if (historyRef.current.length >= 5) {
      let totalVariance = 0;
      const numPoints = keyPoints.length;
      const numFrames = historyRef.current.length;

      for (let i = 0; i < numPoints; i++) {
        let sumX = 0, sumY = 0;
        for (let f = 0; f < numFrames; f++) {
          const p = historyRef.current[f][i];
          if (!p) {
            clearHold();
            setIsStable((s) => (s ? false : s));
            return;
          }
          sumX += p.x;
          sumY += p.y;
        }
        const meanX = sumX / numFrames;
        const meanY = sumY / numFrames;

        let sumSqDiff = 0;
        for (let f = 0; f < numFrames; f++) {
          const p = historyRef.current[f][i];
          const dx = p.x - meanX;
          const dy = p.y - meanY;
          sumSqDiff += (dx * dx + dy * dy);
        }
        totalVariance += sumSqDiff / numFrames;
      }

      const avgVariance = totalVariance / numPoints;

      /** Hysteresis: exit stability only when variance is clearly above threshold (reduces flicker at the cutoff). */
      const breakVariance = varianceThreshold * 1.55;

      if (avgVariance < varianceThreshold) {
        if (stabilityStartRef.current === null) {
          stabilityStartRef.current = Date.now();
        } else {
          const duration = Date.now() - stabilityStartRef.current;
          if (duration >= stableDurationMs) {
            setIsStable((s) => (s ? s : true));
          }
        }
      } else if (avgVariance > breakVariance) {
        clearHold();
        setIsStable((s) => (s ? false : s));
      }
    }
  }, [
    landmarks,
    varianceThreshold,
    stableDurationMs,
    extraLandmarkIndices,
    extraLandmarkVisibilityThreshold,
    visibilityThreshold,
  ]);

  return {
    isStable,
    variance: 0,
    stableDuration: 0,
    isPoseDetected: !!landmarks && landmarks.length > 0
  };
}
